import { useEffect, useState } from "react";
import { useAuth } from "../lib/auth";
import { getOpportunityHistory } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { statusLabel } from "../lib/status-labels";
import type { Opportunity, OpportunityUpdate } from "../types";

export function OpportunityHistoryDialog({
  opportunity,
  onClose,
}: {
  opportunity: Opportunity | null;
  onClose: () => void;
}) {
  const { session } = useAuth();
  const { locale, t } = useI18n();
  const [updates, setUpdates] = useState<OpportunityUpdate[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!opportunity || !session) return;
    let active = true;
    setLoading(true);
    void getOpportunityHistory(session.accessToken, opportunity.id)
      .then((items) => {
        if (active) setUpdates(items);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [opportunity, session]);

  if (!opportunity) return null;
  return (
    <div
      className="dialog-backdrop"
      role="presentation"
      onMouseDown={(event) => event.target === event.currentTarget && onClose()}
    >
      <section
        className="dialog history-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="project-history-title"
      >
        <div className="dialog-title-row">
          <div>
            <span className="eyebrow">
              {opportunity.clientReference.displayName} ·{" "}
              {opportunity.projectName}
            </span>
            <h2 id="project-history-title">{t("projectHistory")}</h2>
          </div>
          <button
            className="icon-button"
            type="button"
            onClick={onClose}
            aria-label={t("cancel")}
          >
            ×
          </button>
        </div>
        {loading ? (
          <p className="field-hint">…</p>
        ) : updates.length ? (
          <div className="project-history-list">
            {updates.map((update) => (
              <article className="history-update" key={update.id}>
                <div className="history-update-heading">
                  <strong>{update.actorName}</strong>
                  <time dateTime={update.occurredAt}>
                    {new Intl.DateTimeFormat(locale, {
                      dateStyle: "medium",
                      timeStyle: "short",
                    }).format(new Date(update.occurredAt))}
                  </time>
                </div>
                <p>
                  {t("statusChanged")}:{" "}
                  {statusLabel(update.previousStatus, locale)} →{" "}
                  <strong>{statusLabel(update.status, locale)}</strong>
                </p>
                {(update.note || update.reason) && (
                  <p>{update.note ?? update.reason}</p>
                )}
                {update.nextCheckInOn && (
                  <small>
                    {t("nextCheckIn")}: {update.nextCheckInOn}
                  </small>
                )}
                {update.attachments.length > 0 && (
                  <div className="history-files">
                    <strong>{t("attachments")}</strong>
                    {update.attachments.map((attachment) =>
                      attachment.downloadUrl ? (
                        <a
                          href={attachment.downloadUrl}
                          key={attachment.id}
                          target="_blank"
                          rel="noreferrer"
                        >
                          📎 {attachment.fileName}
                        </a>
                      ) : (
                        <span key={attachment.id}>
                          📎 {attachment.fileName}
                        </span>
                      ),
                    )}
                  </div>
                )}
              </article>
            ))}
          </div>
        ) : (
          <div className="empty-state">{t("noHistory")}</div>
        )}
      </section>
    </div>
  );
}
